"use client";

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/use-toast";

import { ApiError } from "./http";

const AUTH_TOAST_COOLDOWN_MS = 5000;

let browserQueryClient: QueryClient | undefined;
let lastAuthToastAt = 0;

function isAuthError(error: unknown) {
  if (error instanceof ApiError) {
    return error.status === 401 || error.status === 403;
  }

  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    ((error as { status?: unknown }).status === 401 || (error as { status?: unknown }).status === 403)
  );
}

function showAuthToast(error: unknown) {
  if (typeof window === "undefined" || !isAuthError(error)) {
    return;
  }

  const now = Date.now();
  if (now - lastAuthToastAt < AUTH_TOAST_COOLDOWN_MS) {
    return;
  }

  lastAuthToastAt = now;
  toast({
    title: "Please log in to continue",
    description: "This action requires an authenticated account.",
    variant: "destructive",
  });
}

function shouldRetry(failureCount: number, error: unknown) {
  if (isAuthError(error)) {
    return false;
  }

  if (error instanceof ApiError) {
    return error.status >= 500 && error.status < 600 && failureCount < 2;
  }

  return failureCount < 2;
}

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: error => {
        showAuthToast(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: error => {
        showAuthToast(error);
      },
    }),
  });
}

export function getQueryClient() {
  if (typeof window === "undefined") {
    return makeQueryClient();
  }

  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
}
